// ============================================================================
// TIP DEALER SERVICE
// ============================================================================
// Handles dealer tip transactions with full audit trail
//  PRODUCTION: Supports both PKCash and PlayCoins (user choice)

//  ENTERPRISE: iOS WebView compatible, non-blocking async operations

//  LEGAL: Complete transaction logging for compliance

// ============================================================================

import { db, auth } from '../firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  runTransaction,
  onSnapshot,
} from 'firebase/firestore';
import walletService from './walletService';

// ============================================================================
// TYPES
// ============================================================================

export type TipCurrency = 'playcoins' | 'pkcash';

export interface TipAmount {
  value: number;
  label: string;
  emoji: string;
  description?: string;
}

export interface TipResult {
  success: boolean;
  error?: string;
  newBalance?: number;
  transactionId?: string;
  tipRecordId?: string;
}

export interface DealerTipRecord {
  id: string;
  tableId: string;
  tipperId: string;
  tipperName: string;
  amount: number;
  currency: TipCurrency;
  timestamp: Date;
  dealerImageUrl?: string;
  expiresAt?: Date;
}

export interface TipAnimationData {
  id: string;
  tableId: string;
  tipperId: string;
  tipperName: string;
  amount: number;
  currency: TipCurrency;
  emoji: string;
  startTime: number;
  duration: number;
}

export interface TipStats {
  totalTips: number;
  tipCount: number;
  avgTip: number;
  lastTipTime: Date | null;
  topTipper?: {
    name: string;
    total: number;
  };
}

// ============================================================================
// CONSTANTS
// ============================================================================

// PKCash tip amount presets (real money equivalent)
export const TIP_AMOUNTS_PKCASH: TipAmount[] = [
  { 
    value: 0.25, 
    label: '$0.25', 
    emoji: '',

    description: 'Small tip'
  },
  { 
    value: 0.50, 
    label: '$0.50', 
    emoji: '',

    description: 'Nice tip'
  },
  { 
    value: 1.00, 
    label: '$1.00', 
    emoji: '',

    description: 'Great tip'
  },
  { 
    value: 2.00, 
    label: '$2.00', 
    emoji: '',

    description: 'Generous tip'
  },
  { 
    value: 5.00, 
    label: '$5.00', 
    emoji: '',

    description: 'Royal tip'
  },
];

// PlayCoins tip amount presets (virtual currency)
export const TIP_AMOUNTS_PLAYCOINS: TipAmount[] = [
  { 
    value: 100, 
    label: '100', 
    emoji: '',

    description: 'Small tip'
  },
  { 
    value: 250, 
    label: '250', 
    emoji: '',

    description: 'Nice tip'
  },
  { 
    value: 500, 
    label: '500', 
    emoji: '',

    description: 'Great tip'
  },
  { 
    value: 1000, 
    label: '1K', 
    emoji: '',

    description: 'Generous tip'
  },
  { 
    value: 2500, 
    label: '2.5K', 
    emoji: '',

    description: 'Royal tip'
  },
];

// Tip animation duration in milliseconds
export const TIP_ANIMATION_DURATION = 4000;

// Cooldown between tips (prevent spam) - 5 seconds
const TIP_COOLDOWN_MS = 5000;

// Track last tip time per user (in-memory for performance)
const lastTipTimes = new Map<string, number>();

// Pending tip operations (prevent double-submit)
const pendingTips = new Set<string>();

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Detect if running in iOS WebView (Capacitor)
 */
function isIOSWebView(): boolean {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
  const isCapacitor = !!(window as any).Capacitor;
  return isIOS && isCapacitor;
}

/**
 * Safe currency operation with iOS compatibility
 * iOS sometimes has issues with very small floating point operations
 */
function safeCurrencyRound(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Generate idempotency key for tip transaction
 */
function generateTipIdempotencyKey(
  tipperId: string,
  tableId: string,
  amount: number,
  currency: TipCurrency
): string {
  const timestamp = Math.floor(Date.now() / 1000); // Second-level granularity
  return `tip-${tipperId}-${tableId}-${amount}-${currency}-${timestamp}`;
}

/**
 * Get emoji for tip amount
 */
function getTipEmoji(amount: number, currency: TipCurrency): string {
  const amounts = currency === 'pkcash' ? TIP_AMOUNTS_PKCASH : TIP_AMOUNTS_PLAYCOINS;
  const tipOption = amounts.find(t => t.value === amount);
  if (tipOption) return tipOption.emoji;
  
  // Default emoji based on relative size
  if (currency === 'pkcash') {
    if (amount >= 5) return '';

    if (amount >= 2) return '';

    if (amount >= 1) return '';

    if (amount >= 0.5) return '';

    return '';

  } else {
    if (amount >= 2500) return '';

    if (amount >= 1000) return '';

    if (amount >= 500) return '';

    if (amount >= 250) return '';

    return '';

  }
}

// ============================================================================
// TIP DEALER SERVICE CLASS
// ============================================================================

class TipDealerService {
  // Track active animation subscriptions for cleanup
  private activeSubscriptions: Map<string, () => void> = new Map();

  // ============================================================================
  // MAIN TIP METHOD
  // ============================================================================

  /**
   * Process a dealer tip
   *  PRODUCTION: Full transaction with audit trail

   *  ENTERPRISE: Non-blocking, iOS compatible

   *  LEGAL: Complete logging for compliance

   */
  async tipDealer(
    tableId: string,
    tipperId: string,
    tipperName: string,
    amount: number,
    currency: TipCurrency
  ): Promise<TipResult> {
    // Generate unique key for this tip attempt
    const tipKey = `${tipperId}-${tableId}-${Date.now()}`;

    try {
      // 1. Validate inputs
      if (!tableId || !tipperId || !tipperName) {
        console.error('❌ [TipDealer] Invalid tip parameters');
        return { success: false, error: 'Invalid tip parameters' };
      }

      if (amount <= 0) {
        console.error('❌ [TipDealer] Invalid tip amount:', amount);
        return { success: false, error: 'Invalid tip amount' };
      }

      // 2. Check for duplicate submission
      if (pendingTips.has(tipKey)) {
        console.warn('⚠️ [TipDealer] Duplicate tip submission blocked');
        return { success: false, error: 'Tip already processing' };
      }

      // 3. Check cooldown
      const cooldownKey = `${tipperId}-tip`;
      const lastTipTime = lastTipTimes.get(cooldownKey) || 0;
      const now = Date.now();
      
      if (now - lastTipTime < TIP_COOLDOWN_MS) {
        const remaining = Math.ceil((TIP_COOLDOWN_MS - (now - lastTipTime)) / 1000);
        console.log(`⏳ [TipDealer] Cooldown active: ${remaining}s remaining`);
        return { success: false, error: `Wait ${remaining}s before tipping again` };
      }

      // 4. Mark as pending
      pendingTips.add(tipKey);

      // 5. Get current wallet balance
      const wallet = await walletService.getWallet(tipperId);
      if (!wallet) {
        pendingTips.delete(tipKey);
        return { success: false, error: 'Wallet not found' };
      }

      // 6. Check if user can afford the tip
      const currentBalance = currency === 'pkcash' ? wallet.pkCash : wallet.playCoins;
      const safeAmount = safeCurrencyRound(amount);
      
      if (currentBalance < safeAmount) {
        pendingTips.delete(tipKey);
        const currencyLabel = currency === 'pkcash' ? 'PKCash' : 'PlayCoins';
        return { 
          success: false, 
          error: `Insufficient ${currencyLabel} balance (have ${currentBalance.toFixed(2)}, need ${safeAmount.toFixed(2)})` 
        };
      }

      console.log(`💵 [TipDealer] Processing tip: ${safeAmount} ${currency} from ${tipperName} (${tipperId.substring(0,8)})`);

      // 7. Execute tip transaction
      const result = await this.executeTipTransaction(
        tipperId,
        tipperName,
        tableId,
        safeAmount,
        currency,
        wallet
      );

      if (result.success) {
        // Update cooldown
        lastTipTimes.set(cooldownKey, now);
        console.log(`✅ [TipDealer] Tip successful! New balance: ${result.newBalance}`);
      }

      return result;

    } catch (error: any) {
      console.error('❌ [TipDealer] Error processing tip:', error);
      
      // Better error messages for iOS
      let errorMessage = 'Failed to process tip';
      
      if (error.message?.includes('network') || error.message?.includes('Network')) {
        errorMessage = 'Network error - check your connection';
      } else if (error.message?.includes('Insufficient')) {
        errorMessage = error.message;
      } else if (error.message?.includes('permission') || error.message?.includes('Permission')) {
        errorMessage = 'Permission denied - please try again';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return { 
        success: false, 
        error: errorMessage
      };
    } finally {
      // Always remove from pending
      pendingTips.delete(tipKey);
    }
  }

  // ============================================================================
  // TRANSACTION EXECUTION
  // ============================================================================

  /**
   * Execute the tip transaction with Firestore transaction for atomicity
   *  ENTERPRISE: Atomic operation prevents race conditions

   */
  private async executeTipTransaction(
    tipperId: string,
    tipperName: string,
    tableId: string,
    amount: number,
    currency: TipCurrency,
    wallet: any
  ): Promise<TipResult> {
    try {
      const walletRef = doc(db, 'wallets', tipperId);
      const balanceField = currency === 'pkcash' ? 'pkCash' : 'playCoins';
      
      // Use Firestore transaction for atomic update
      const transactionResult = await runTransaction(db, async (transaction) => {
        // Re-read wallet inside transaction for consistency
        const walletSnapshot = await transaction.get(walletRef);
        if (!walletSnapshot.exists()) {
          throw new Error('Wallet not found');
        }

        const currentWallet = walletSnapshot.data();
        const currentBalance = currentWallet[balanceField] || 0;

        // Double-check balance (could have changed since initial check)
        if (currentBalance < amount) {
          throw new Error('Insufficient balance');
        }

        // Calculate new balance with safe rounding
        const newBalance = safeCurrencyRound(currentBalance - amount);

        // Update wallet atomically
        transaction.update(walletRef, {
          [balanceField]: newBalance,
          updatedAt: new Date(),
        });

        return { newBalance };
      });

      // Log transaction for audit trail (outside main transaction for performance)
      const transactionId = await this.logTipTransaction(
        tipperId,
        tipperName,
        tableId,
        amount,
        currency
      );

      // Create tip animation record for table display
      const tipRecordId = await this.createTipAnimationRecord(
        tableId,
        tipperId,
        tipperName,
        amount,
        currency
      );

      return {
        success: true,
        newBalance: transactionResult.newBalance,
        transactionId,
        tipRecordId,
      };

    } catch (error: any) {
      console.error('❌ [TipDealer] Transaction error:', error);
      throw error;
    }
  }

  // ============================================================================
  // AUDIT LOGGING
  // ============================================================================

  /**
   * Log tip transaction for audit trail
   *  LEGAL: Complete transaction record for compliance

   */
  private async logTipTransaction(
    tipperId: string,
    tipperName: string,
    tableId: string,
    amount: number,
    currency: TipCurrency
  ): Promise<string> {
    try {
      const idempotencyKey = generateTipIdempotencyKey(tipperId, tableId, amount, currency);
      
      const transactionData = {
        userId: tipperId,
        type: 'dealer_tip',
        currency,
        amount: -amount, // Negative = deduction from user's wallet
        description: `Dealer tip at table ${tableId.substring(0, 8)}`,
        metadata: {
          tableId,
          tipperName,
          tipType: 'dealer',
          idempotencyKey,
        },
        status: 'completed',
        timestamp: Timestamp.now(),
        createdAt: Timestamp.now(),
      };

      const docRef = await addDoc(collection(db, 'transactions'), transactionData);
      console.log(`📝 [TipDealer] Transaction logged: ${docRef.id}`);
      return docRef.id;

    } catch (error) {
      console.error('❌ [TipDealer] Error logging transaction:', error);
      // Don't throw - tip was already processed, this is just audit
      return '';
    }
  }

  /**
   * Create a tip animation record for table display
   *  ENTERPRISE: Real-time sync for all players to see animation

   */
  private async createTipAnimationRecord(
    tableId: string,
    tipperId: string,
    tipperName: string,
    amount: number,
    currency: TipCurrency
  ): Promise<string> {
    try {
      const emoji = getTipEmoji(amount, currency);
      const now = Date.now();
      const expiresAt = new Date(now + TIP_ANIMATION_DURATION + 1000); // Extra 1s buffer
      
      const tipRecord = {
        tableId,
        tipperId,
        tipperName,
        amount,
        currency,
        emoji,
        duration: TIP_ANIMATION_DURATION,
        timestamp: Timestamp.now(),
        expiresAt: Timestamp.fromDate(expiresAt),
      };

      const docRef = await addDoc(collection(db, 'dealerTips'), tipRecord);
      console.log(`📝 [TipDealer] Tip animation record created: ${docRef.id}`);
      return docRef.id;

    } catch (error) {
      console.error('❌ [TipDealer] Error creating tip animation record:', error);
      // Don't throw - tip was processed, animation is just visual
      return '';
    }
  }

  // ============================================================================
  // ANIMATION SUBSCRIPTION
  // ============================================================================

  /**
   * Subscribe to tip animations for a table
   * Returns unsubscribe function
   *  ENTERPRISE: Real-time updates for all players

   */
  subscribeToTipAnimations(
    tableId: string,
    onTipAnimations: (animations: TipAnimationData[]) => void
  ): () => void {
    // Clean up existing subscription for this table
    const existingUnsub = this.activeSubscriptions.get(`tip-${tableId}`);
    if (existingUnsub) {
      existingUnsub();
    }

    console.log('💵 [TipDealer] Subscribing to tip animations for table:', tableId);
    
    // Query for active tips (not expired)
    const q = query(
      collection(db, 'dealerTips'),
      where('tableId', '==', tableId)
    );

    let subscriptionFailed = false;
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const now = Date.now();
      const animations: TipAnimationData[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();

        const expiresAt = data.expiresAt?.toDate?.()?.getTime() || 0;
        const isExpired = expiresAt <= now;

        // Only include non-expired animations
        if (!isExpired) {
          animations.push({
            id: doc.id,
            tableId: data.tableId,
            tipperId: data.tipperId,
            tipperName: data.tipperName,
            amount: data.amount,
            currency: data.currency,
            emoji: data.emoji || getTipEmoji(data.amount, data.currency),
            startTime: data.timestamp?.toDate?.()?.getTime() || Date.now(),
            duration: data.duration || TIP_ANIMATION_DURATION,
          });
        }
      });

      // Sort by startTime (newest first)
      animations.sort((a, b) => b.startTime - a.startTime);

      if (animations.length > 0) {
        console.log('💵 [TipDealer] Active tip animations:', animations.length);
      }

      onTipAnimations(animations);
    }, (error) => {
      //  FIX: Only log permission errors once, don't spam console

      if (!subscriptionFailed) {
        subscriptionFailed = true;
        console.warn('⚠️ [TipDealer] Tip animation subscription unavailable (permissions) - tips will still work when sent');
      }
      onTipAnimations([]);
    });

    this.activeSubscriptions.set(`tip-${tableId}`, unsubscribe);
    return unsubscribe;
  }

  /**
   * Unsubscribe from table tip animations
   */
  unsubscribeFromTipAnimations(tableId: string): void {
    const unsub = this.activeSubscriptions.get(`tip-${tableId}`);
    if (unsub) {
      unsub();
      this.activeSubscriptions.delete(`tip-${tableId}`);
      console.log('💵 [TipDealer] Unsubscribed from tip animations for:', tableId);
    }
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Check if user can afford a specific tip amount
   */
  async canAffordTip(
    userId: string,
    amount: number,
    currency: TipCurrency
  ): Promise<boolean> {
    try {
      const wallet = await walletService.getWallet(userId);
      if (!wallet) return false;

      const balance = currency === 'pkcash' ? wallet.pkCash : wallet.playCoins;
      return balance >= amount;
    } catch {
      return false;
    }
  }

  /**
   * Get user's current balance for tip currency
   */
  async getBalance(userId: string, currency: TipCurrency): Promise<number> {
    try {
      const wallet = await walletService.getWallet(userId);
      if (!wallet) return 0;
      return currency === 'pkcash' ? wallet.pkCash : wallet.playCoins;
    } catch {
      return 0;
    }
  }

  /**
   * Get both balances for currency selection UI
   */
  async getBothBalances(userId: string): Promise<{
    playCoins: number;
    pkCash: number;
  }> {
    try {
      const wallet = await walletService.getWallet(userId);
      return {
        playCoins: wallet?.playCoins || 0,
        pkCash: wallet?.pkCash || 0,
      };
    } catch {
      return { playCoins: 0, pkCash: 0 };
    }
  }

  /**
   * Get remaining cooldown in seconds (0 if no cooldown)
   */
  getCooldownRemaining(userId: string): number {
    const cooldownKey = `${userId}-tip`;
    const lastTipTime = lastTipTimes.get(cooldownKey) || 0;
    const elapsed = Date.now() - lastTipTime;
    return Math.max(0, Math.ceil((TIP_COOLDOWN_MS - elapsed) / 1000));
  }

  /**
   * Check if user is on cooldown
   */
  isOnCooldown(userId: string): boolean {
    return this.getCooldownRemaining(userId) > 0;
  }

  /**
   * Get tip amounts based on currency
   */
  getTipAmounts(currency: TipCurrency): TipAmount[] {
    return currency === 'pkcash' ? TIP_AMOUNTS_PKCASH : TIP_AMOUNTS_PLAYCOINS;
  }

  /**
   * Format tip amount for display
   */
  formatTipAmount(amount: number, currency: TipCurrency): string {
    if (currency === 'pkcash') {
      return `$${amount.toFixed(2)}`;
    }
    return amount >= 1000 ? `${(amount / 1000).toFixed(amount % 1000 === 0 ? 0 : 1)}K` : amount.toString();
  }

  /**
   * Format currency label for display
   */
  formatCurrencyLabel(currency: TipCurrency): string {
    return currency === 'pkcash' ? 'PKCash' : 'PlayCoins';
  }

  /**
   * Get tip emoji for amount
   */
  getTipEmoji(amount: number, currency: TipCurrency): string {
    return getTipEmoji(amount, currency);
  }

  /**
   * Get tip statistics for a table (for admin/analytics)
   */
  async getTableTipStats(tableId: string): Promise<TipStats> {
    try {
      const q = query(
        collection(db, 'dealerTips'),
        where('tableId', '==', tableId),
        orderBy('timestamp', 'desc'),
        limit(100) // Last 100 tips
      );

      const snapshot = await getDocs(q);
      
      let totalTips = 0;
      let tipCount = 0;
      let lastTipTime: Date | null = null;
      const tipperTotals: Map<string, { name: string; total: number }> = new Map();

      snapshot.forEach((doc) => {
        const data = doc.data();
        totalTips += data.amount || 0;
        tipCount++;
        
        if (!lastTipTime) {
          lastTipTime = data.timestamp?.toDate?.() || null;
        }

        // Track per-tipper totals
        const existing = tipperTotals.get(data.tipperId) || { name: data.tipperName, total: 0 };
        existing.total += data.amount || 0;
        tipperTotals.set(data.tipperId, existing);
      });

      // Find top tipper
      let topTipper: { name: string; total: number } | undefined;
      tipperTotals.forEach((value) => {
        if (!topTipper || value.total > topTipper.total) {
          topTipper = value;
        }
      });

      return {
        totalTips,
        tipCount,
        avgTip: tipCount > 0 ? totalTips / tipCount : 0,
        lastTipTime,
        topTipper,
      };
    } catch (error) {
      console.error('❌ [TipDealer] Error getting tip stats:', error);
      return {
        totalTips: 0,
        tipCount: 0,
        avgTip: 0,
        lastTipTime: null,
      };
    }
  }

  /**
   * Cleanup expired tip animation records (call periodically)
   */
  async cleanupExpiredTips(tableId: string): Promise<void> {
    try {
      const q = query(
        collection(db, 'dealerTips'),
        where('tableId', '==', tableId)
      );

      const snapshot = await getDocs(q);
      const deletePromises: Promise<void>[] = [];
      const now = Date.now();
      
      snapshot.forEach((doc) => {
        const expiresAt = doc.data().expiresAt?.toDate?.()?.getTime() || 0;
        if (expiresAt <= now) {
          deletePromises.push(
            import('firebase/firestore').then(({ deleteDoc }) => deleteDoc(doc.ref))
          );
        }
      });
      
      if (deletePromises.length > 0) {
        await Promise.all(deletePromises);
        console.log(`🧹 [TipDealer] Cleaned up ${deletePromises.length} expired tip records`);
      }
    } catch (error) {
      console.error('❌ [TipDealer] Cleanup error:', error);
    }
  }

  /**
   * Stop all active subscriptions (cleanup on unmount)
   */
  stopAll(): void {
    this.activeSubscriptions.forEach((unsubscribe, key) => {
      unsubscribe();
      console.log(`🛑 [TipDealer] Stopped subscription: ${key}`);
    });
    this.activeSubscriptions.clear();
  }
}

// ============================================================================
// EXPORT
// ============================================================================

const tipDealerService = new TipDealerService();
export default tipDealerService;